import { detectPlatform } from './detect';
import { IMAGE_ACCEPT, IMAGE_EXTENSIONS, mimeFromFileName } from './images';

export async function openExternalUrl(url: string): Promise<void> {
  if (detectPlatform() === 'tauri') {
    try {
      const { open } = await import('@tauri-apps/plugin-shell');
      await open(url);
      return;
    } catch {
      // fall through
    }
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

export async function pickLocalImageFile(): Promise<File | null> {
  if (detectPlatform() === 'tauri') {
    try {
      const { open } = await import('@tauri-apps/plugin-dialog');
      const selected = await open({
        multiple: false,
        filters: [{ name: 'Images', extensions: [...IMAGE_EXTENSIONS] }],
      });
      if (!selected || Array.isArray(selected)) return null;
      const { readFile } = await import('@tauri-apps/plugin-fs');
      const bytes = await readFile(selected);
      const ext = selected.split('.').pop()?.toLowerCase() ?? 'png';
      const mime = mimeFromFileName(`x.${ext}`) ?? 'image/png';
      return new File([bytes], `image.${ext}`, { type: mime });
    } catch {
      // fall through to web picker
    }
  }

  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = IMAGE_ACCEPT;
    input.onchange = () => {
      const file = input.files?.[0] ?? null;
      resolve(file);
    };
    input.click();
  });
}

export async function showSystemNotification(title: string, body: string): Promise<boolean> {
  if (detectPlatform() === 'tauri') {
    try {
      const { isPermissionGranted, requestPermission, sendNotification } = await import(
        '@tauri-apps/plugin-notification'
      );
      let granted = await isPermissionGranted();
      if (!granted) {
        const permission = await requestPermission();
        granted = permission === 'granted';
      }
      if (!granted) return false;
      sendNotification({ title, body });
      return true;
    } catch {
      return false;
    }
  }

  if (!('Notification' in window)) return false;
  if (Notification.permission === 'denied') return false;
  if (Notification.permission === 'default') {
    const result = await Notification.requestPermission();
    if (result !== 'granted') return false;
  }
  // eslint-disable-next-line no-new
  new Notification(title, { body });
  return true;
}

export function notificationsSupported(): boolean {
  if (detectPlatform() === 'tauri') return true;
  return 'Notification' in window;
}

export function backgroundNotificationsGuaranteed(): boolean {
  // Honest limitation: browsers cannot reliably deliver when tab is killed;
  // Tauri can do better for desktop but still no push server in v1.
  return detectPlatform() === 'tauri';
}
